/*
 *  This document is the CONFIDENTIAL PROPERTY of Corero Network Security, Inc.
 *  Copyright (C) 2022-2025 by Corero Network Security, Inc. All rights reserved.
 *  Notice of copyright on this document does not constitute publication.
 */

import Flush from '@corero/ssp-ui-components/core/flush';
import sharedConfig from '@corero/ssp-ui-components/core/sharedConfig';
import {requester} from '@corero/ssp-ui-components/core/requester';
import {config} from 'corero-ssp-ui/src/core/app-config';
import msg from '@corero/ssp-ui-components/core/Notify';
import {useEffect, useState} from "react";

/**
 * @typedef {Object} ThemeConfiguration - The data containing title, subtitle, text color code, button color code, button foreground color code and error text color code keys.
 * @property {string} login_title - Title at the login page.
 * @property {string} login_sub_title - Sub Title at the login page.
 * @property {string} login_color_text -  text color at login and forgot password page.
 * @property {string} login_color_error_text - error text color at login and forgot password page.
 * @property {string} login_color_button - button color at login and forgot password page.
 * @property {string} login_color_btn_foreground - button foreground color at login and forgot password page.
 * @property {string} login_color_cancel_btn - the button color for cancelling
 * @property {string} login_color_cancel_btn_foreground - the cancel button text color
 * @property {string} login_button_border_color - border color of login button
 * @property {string} login_button_border_width - border width of login button
 * @property {string} page_title_text - the cancel button text color
 */

let themeConfigurationData = {};
let termsOfServiceData;
let brandingFlag;
let ssoLoginConfigData =[];
let saml2LoginConfigData = []
let ssoDisabledTenantUserCreation = {};
let appConfigs = {};
let auditConfigs = {};
let passwordConfigs = {};

let methods = {
    fetchAuditConfigs() {
        this.fetchConfigs('audit', auditConfigs)
    },
    fetchPasswordConfigs() {
        this.fetchConfigs('password', passwordConfigs)
    },
    fetchAppConfigs() {
        this.fetchConfigs('app', appConfigs)
    },
    fetchConfigs(type, configs) {
        requester.get(`${config.apis.config}/${type}`)
            .then((res) => {
                res.forEach(conf => {
                    configs[conf.name] = conf;
                });
                this.changed();
            })
    },
    saveConfigSettings(data, type) {
        if (!data) return;
        requester.put(`${config.apis.config}/${type}`, data)
            .then(() => {
                msg.info('The config was saved successfully');
            })
    },
    saveAuditConfigSettings(data) {
        this.saveConfigSettings(data, 'audit');
    },
    savePasswordConfigSettings(data) {
        this.saveConfigSettings(Object.values(data), 'password');
    },
    getAuditConfigs() {
        return auditConfigs;
    },
    getPasswordConfigs() {
        return passwordConfigs;
    },
    getAppConfigs() {
        return appConfigs;
    },

    /**
     * @param {Object} data - The data containing title, subtitle, text color code, button color code, button foreground color code and error text color code keys.
     * @param {string} data.login_title - Title at the login page.
     * @param {string} data.login_sub_title - Sub Title at the login page.
     * @param {string} data.login_color_text -  text color at login and forgot password page.
     * @param {string} data.login_color_error_text - error text color at login and forgot password page.
     * @param {string} data.login_color_button - button color at login and forgot password page.
     * @param {string} data.login_color_btn_foreground - button foreground color at login and forgot password page.
     * @param {string} data.login_color_cancel_btn - the button color for cancelling
     * @param {string} data.login_color_cancel_btn_foreground - the cancel button text color
     * @param {string} data.page_title_text - the cancel button text color
     */
    updateThemeConfiguration(data) {
        requester.put(config.apis.themeConfiguration, data)
            .then((res) => {
                msg.info('Theme updated successfully');
                this.fetchThemeConfiguration()
                return res
            });
    },

    /**
     * @param {Object} data - The data containing Terms of Service Url and Terms of Service text.
     * @param {string} data.title - Terms of Service Url at the login page.
     * @param {string} data.subTitle - Terms of service text at the login page.
     */
    updateTermsOfService(data){
        requester.put(config.apis.termsOfService, data)
            .then((res) => {
                msg.info('Terms of service Updated successfully');
                return res
            });
    },

    /**
     * Fetches theme configuration and triggers on change listener when successful
     */
    fetchThemeConfiguration(customErrorHandler = undefined) {
        requester.get(sharedConfig.apis.theme + '/themeConfiguration', null, null, {customErrorHandlerFor503: customErrorHandler !== undefined}, false,  'application/json', customErrorHandler)
        .then((res) => {
            themeConfigurationData = res;
            this.changed();
        });
    },

    fetchSsoLoginConfig(customErrorHandler = undefined) {
        requester.get(sharedConfig.apis.ssoConfigLogin, null, null, {customErrorHandlerFor503: customErrorHandler !== undefined}, false,  'application/json', customErrorHandler)
            .then((res) => {
                ssoLoginConfigData = res;
                this.changed();
            })
    },

    fetchSaml2LoginConfig(customErrorHandler = undefined) {
        requester.get(sharedConfig.apis.saml2ConfigLogin, null, null, {customErrorHandlerFor503: customErrorHandler !== undefined}, false,  'application/json', customErrorHandler)
            .then((res) => {
                saml2LoginConfigData = res;
                this.changed();
            })
    },

    fetchSsoDisabledTenantUserCreation() {
        requester.get(config.apis.ssoDisableTenantUserCreation)
            .then((res) => {
                ssoDisabledTenantUserCreation = res;
                this.changed();
            })
    },

    fetchTermsOfServiceUrlData(customErrorHandler = undefined) {
        requester.get(sharedConfig.apis.theme + '/serviceTerm', null, null, {customErrorHandlerFor503: customErrorHandler !== undefined}, false,  'application/json', customErrorHandler)
            .then((res) => {
                termsOfServiceData = res;
                this.changed();
            });
    },

    fetchBrandingFlag(customErrorHandler = undefined) {
        requester.get(sharedConfig.apis.theme + '/getBrandingFlag', null, null, {customErrorHandlerFor503: customErrorHandler !== undefined}, false,  'application/json', customErrorHandler)
            .then((res) => {
                brandingFlag = res;
                this.changed();
            });
    },

    /**
     * @returns {ThemeConfiguration} themeConfiguration
     */
    getThemeConfiguration() {
        return themeConfigurationData;
    },

    getTermsOfServiceData() {
        return termsOfServiceData;
    },

    getBrandingFlag() {
        return brandingFlag;
    },

    getSsoLoginConfig() {
        return ssoLoginConfigData;
    },

    getSaml2LoginConfig() {
        return saml2LoginConfigData;
    },

    getSsoDisabledTenantUserCreation() {
        return ssoDisabledTenantUserCreation;
    }
};

let {actions, store} = Flush.store(methods);

/**
 * Hook for consuming theme configuration
 * @returns {[ThemeConfiguration|{},((value: (((prevState: {}) => {}) | {})) => void)]}
 */
const useThemeConfig = () => {
    const [themeConfig, setThemeConfig] = useState({})
    useEffect(() => {
        const onChangeListener = () => {
            const nextThemeConfig = store.getThemeConfiguration()
            setThemeConfig(nextThemeConfig)
        }
        store.addChangeListener(onChangeListener)
        actions.fetchThemeConfiguration(() => {});
        return () => store.removeChangeListener(onChangeListener)
    }, [])
    return [themeConfig, setThemeConfig]
}

export { actions, store, useThemeConfig };
